// Modal de criar/editar atividade + painel embedado de Recursos alocados.
// Wave 2 (Antd) — 2026-06-01.

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Modal, Form, Input, Select, DatePicker, InputNumber, Slider, Checkbox, Button,
  Row, Col, Alert, Typography, Space, Card, Tag, Empty, Spin, App as AntApp,
  Popconfirm, Segmented,
} from 'antd';
import { PlusOutlined, DeleteOutlined, TeamOutlined, ToolOutlined, ShoppingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import type { Contrato, Atividade, AlocacaoAtividade, RecursosBundle } from './types';
import { STATUS_ATIVIDADE, PRIORIDADE_ATIVIDADE, TIPO_ATIVIDADE } from './types';
import { fmtData, getToken } from './utils';

const { TextArea } = Input;

export function ModalAtividade({ contrato, atividade, atividadesDisp, onFechar, onSalvo, onCriado }: {
  contrato: Contrato;
  atividade: Atividade | null;
  atividadesDisp: Atividade[];
  onFechar: () => void;
  onSalvo: () => void;
  onCriado?: (nova: Atividade) => void;
}) {
  return <AntApp>
    <ModalAtividadeInner contrato={contrato} atividade={atividade}
      atividadesDisp={atividadesDisp} onFechar={onFechar} onSalvo={onSalvo} onCriado={onCriado} />
  </AntApp>;
}

function ModalAtividadeInner({ contrato, atividade, atividadesDisp, onFechar, onSalvo, onCriado }: {
  contrato: Contrato;
  atividade: Atividade | null;
  atividadesDisp: Atividade[];
  onFechar: () => void;
  onSalvo: () => void;
  onCriado?: (nova: Atividade) => void;
}) {
  const { message } = AntApp.useApp();
  const editando = atividade !== null;
  const [form, setForm] = useState({
    titulo: atividade?.titulo || '',
    descricao: atividade?.descricao || '',
    tipo: atividade?.tipo || 'tarefa',
    status: atividade?.status || 'pendente',
    prioridade: atividade?.prioridade || 'media',
    data_inicio_prevista: (atividade?.data_inicio_prevista || '').slice(0, 10),
    data_termino_previsto: (atividade?.data_termino_previsto || '').slice(0, 10),
    data_inicio_real: (atividade?.data_inicio_real || '').slice(0, 10),
    data_termino_real: (atividade?.data_termino_real || '').slice(0, 10),
    duracao_dias: atividade?.duracao_dias?.toString() || '',
    progresso_pct: atividade?.progresso_pct ?? 0,
    observacoes: atividade?.observacoes || '',
  });
  const [predecessoras, setPredecessoras] = useState<string[]>(atividade?.predecessoras || []);
  const [atribuicoes, setAtribuicoes] = useState<{ email: string; papel: string }[]>(
    atividade?.atribuicoes || (contrato.responsavel_email
      ? [{ email: contrato.responsavel_email, papel: 'responsavel' }]
      : [])
  );
  const [usuarios, setUsuarios] = useState<{ email: string; nome: string }[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/admin/usuarios`,
          { headers: { Authorization: token } });
        const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios((Array.isArray(data) ? data : [])
          .filter((u: any) => u.enabled !== false)
          .map((u: any) => ({ email: u.email, nome: u.nome || u.email })));
      } catch { setUsuarios([]); }
    })();
  }, []);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  async function salvar() {
    if (!form.titulo.trim()) { setErro('Título é obrigatório'); return; }
    setSalvando(true); setErro('');
    try {
      const token = await getToken();
      const payload: any = {
        action: editando ? 'op_atualizar_atividade' : 'op_criar_atividade',
        contrato_id: contrato.id,
        titulo: form.titulo.trim(),
        descricao: form.descricao || null,
        tipo: form.tipo,
        status: form.status,
        prioridade: form.prioridade,
        data_inicio_prevista: form.data_inicio_prevista || null,
        data_termino_previsto: form.data_termino_previsto || null,
        data_inicio_real: form.data_inicio_real || null,
        data_termino_real: form.data_termino_real || null,
        duracao_dias: form.duracao_dias ? parseInt(form.duracao_dias, 10) : null,
        progresso_pct: form.progresso_pct,
        observacoes: form.observacoes || null,
        predecessoras,
      };
      if (editando) {
        payload.id = atividade!.id;
      } else {
        payload.atribuicoes = atribuicoes;
      }
      const resp = await axios.post(`${API_URL}/comercial/propostas-geradas`, payload,
        { headers: { Authorization: token } });
      const novaAtividade = resp.data?.body ? JSON.parse(resp.data.body) : resp.data;

      if (editando) {
        const antigos = (atividade?.atribuicoes || []).map(x => x.email);
        const novos = atribuicoes.map(x => x.email);
        for (const e of novos.filter(x => !antigos.includes(x))) {
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_atribuir_atividade', atividade_id: atividade!.id, email: e },
            { headers: { Authorization: token } });
        }
        for (const e of antigos.filter(x => !novos.includes(x))) {
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_remover_atribuicao', atividade_id: atividade!.id, email: e },
            { headers: { Authorization: token } });
        }
      }

      if (!editando && onCriado && novaAtividade?.id) {
        onCriado(novaAtividade);
      } else {
        message.success('Salvo.');
        onSalvo();
      }
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message || 'Erro ao salvar');
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onFechar} width={800}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        {editando ? '✏️ Editar atividade' : '📌 Nova atividade'}
      </Typography.Title>}
      onOk={salvar} confirmLoading={salvando}
      okText={editando ? 'Salvar alterações' : 'Criar atividade'}
      okButtonProps={{ disabled: !form.titulo.trim() }}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
      <Form layout="vertical" disabled={salvando}>
        <Row gutter={10}>
          <Col span={24}>
            <Form.Item label="Título *" required>
              <Input value={form.titulo} autoFocus
                onChange={e => setForm({ ...form, titulo: e.target.value })} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Descrição">
              <TextArea value={form.descricao}
                onChange={e => setForm({ ...form, descricao: e.target.value })}
                autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>
          </Col>

          <Col xs={12} md={6}>
            <Form.Item label="Tipo">
              <Select value={form.tipo} onChange={(v) => setForm({ ...form, tipo: v })}
                options={Object.entries(TIPO_ATIVIDADE).map(([k, v]) => ({
                  value: k, label: `${v.emoji} ${v.label}`,
                }))} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Prioridade">
              <Select value={form.prioridade} onChange={(v) => setForm({ ...form, prioridade: v })}
                options={Object.entries(PRIORIDADE_ATIVIDADE).map(([k, v]) => ({
                  value: k, label: `${v.emoji} ${v.label}`,
                }))} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Status">
              <Select value={form.status} onChange={(v) => setForm({ ...form, status: v })}
                options={Object.entries(STATUS_ATIVIDADE).map(([k, v]) => ({ value: k, label: v.label }))} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Duração (dias)">
              <InputNumber min={0} value={form.duracao_dias ? parseInt(form.duracao_dias, 10) : null}
                onChange={(v) => setForm({ ...form, duracao_dias: v != null ? String(v) : '' })}
                style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={12} md={6}>
            <Form.Item label="Início previsto">
              <DatePicker value={form.data_inicio_prevista ? dayjs(form.data_inicio_prevista) : null}
                onChange={(d) => setForm({ ...form, data_inicio_prevista: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Término previsto">
              <DatePicker value={form.data_termino_previsto ? dayjs(form.data_termino_previsto) : null}
                onChange={(d) => setForm({ ...form, data_termino_previsto: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Início real">
              <DatePicker value={form.data_inicio_real ? dayjs(form.data_inicio_real) : null}
                onChange={(d) => setForm({ ...form, data_inicio_real: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Término real">
              <DatePicker value={form.data_termino_real ? dayjs(form.data_termino_real) : null}
                onChange={(d) => setForm({ ...form, data_termino_real: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label={`Progresso (${form.progresso_pct}%)`}>
              <Slider min={0} max={100} step={5} value={form.progresso_pct}
                onChange={(v) => setForm({ ...form, progresso_pct: v })} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Responsáveis">
              <Select mode="multiple"
                value={atribuicoes.map(a => a.email)}
                onChange={(emails: string[]) => {
                  const mapaAntigo: Record<string, string> = {};
                  for (const a of atribuicoes) mapaAntigo[a.email] = a.papel;
                  setAtribuicoes(emails.map(e => ({ email: e, papel: mapaAntigo[e] || 'responsavel' })));
                }}
                placeholder="Selecione 1 ou mais usuários…"
                showSearch allowClear optionFilterProp="label"
                maxTagCount="responsive"
                options={usuarios.map(u => ({ label: `${u.nome} (${u.email})`, value: u.email }))} />
            </Form.Item>
          </Col>

          {atividadesDisp.length > 0 && (
            <Col span={24}>
              <Form.Item label="Predecessoras (esta atividade só inicia depois)">
                <Card size="small" style={{ background: '#FAFBFF', maxHeight: 160, overflowY: 'auto' }}
                  styles={{ body: { padding: 8 } }}>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {atividadesDisp.map(p => (
                      <Checkbox key={p.id}
                        checked={predecessoras.includes(p.id)}
                        onChange={e => {
                          if (e.target.checked) setPredecessoras([...predecessoras, p.id]);
                          else setPredecessoras(predecessoras.filter(x => x !== p.id));
                        }}>
                        <Space size={6}>
                          <span>{TIPO_ATIVIDADE[p.tipo || '']?.emoji || '📌'}</span>
                          <Typography.Text>{p.titulo}</Typography.Text>
                          <Typography.Text style={{ fontSize: 10, color: STATUS_ATIVIDADE[p.status]?.cor || '#888' }}>
                            {STATUS_ATIVIDADE[p.status]?.label || p.status}
                          </Typography.Text>
                        </Space>
                      </Checkbox>
                    ))}
                  </Space>
                </Card>
              </Form.Item>
            </Col>
          )}

          <Col span={24}>
            <Form.Item label="Observações">
              <TextArea value={form.observacoes}
                onChange={e => setForm({ ...form, observacoes: e.target.value })}
                autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>
          </Col>

          {/* Recursos alocados */}
          {editando && atividade ? (
            <Col span={24}>
              <RecursosAtividade
                atividadeId={atividade.id}
                inicioPadrao={form.data_inicio_prevista}
                fimPadrao={form.data_termino_previsto} />
            </Col>
          ) : (
            <Col span={24}>
              <Alert type="info"
                message={<><strong>Recursos (pessoas / veículos / equipamentos)</strong>: clique
                  em "Criar atividade" abaixo. O modal continua aberto pra você alocar.</>} />
            </Col>
          )}

          {erro && (
            <Col span={24}>
              <Alert type="error" message={erro} showIcon style={{ marginTop: 6 }} />
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
}

// ════════════════ RECURSOS ALOCADOS À ATIVIDADE ════════════════

let _recursosCache: { ts: number; data: RecursosBundle } | null = null;
async function carregarRecursosBundle(): Promise<RecursosBundle> {
  if (_recursosCache && Date.now() - _recursosCache.ts < 5 * 60 * 1000) {
    return _recursosCache.data;
  }
  try {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'op_listar_recursos' },
      { headers: { Authorization: token } });
    const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
    _recursosCache = { ts: Date.now(), data: d };
    return d;
  } catch {
    return { pessoas: [], pessoas_externas: [], patrimonios: [] };
  }
}

function RecursosAtividade({ atividadeId, inicioPadrao, fimPadrao }: {
  atividadeId: string;
  inicioPadrao: string;
  fimPadrao: string;
}) {
  const { message } = AntApp.useApp();
  const [alocacoes, setAlocacoes] = useState<AlocacaoAtividade[]>([]);
  const [recursos, setRecursos] = useState<RecursosBundle>({ pessoas: [], pessoas_externas: [], patrimonios: [] });
  const [loading, setLoading] = useState(true);
  const [adicionando, setAdicionando] = useState(false);
  const [tipo, setTipo] = useState<'pessoa' | 'patrimonio' | 'aluguel'>('pessoa');
  const [pessoaChave, setPessoaChave] = useState('');
  const [patrimonioId, setPatrimonioId] = useState('');
  const [descricaoAluguel, setDescricaoAluguel] = useState('');
  const [dataInicio, setDataInicio] = useState(inicioPadrao);
  const [dataFim, setDataFim] = useState(fimPadrao);
  const [horasDia, setHorasDia] = useState(8);

  useEffect(() => {
    Promise.all([recarregar(), carregarRecursosBundle().then(setRecursos)]);
    // eslint-disable-next-line
  }, [atividadeId]);

  useEffect(() => { if (!adicionando) setDataInicio(inicioPadrao); }, [inicioPadrao, adicionando]);
  useEffect(() => { if (!adicionando) setDataFim(fimPadrao); }, [fimPadrao, adicionando]);

  async function recarregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_alocacoes', atividade_id: atividadeId },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setAlocacoes(Array.isArray(d) ? d : []);
    } finally { setLoading(false); }
  }

  async function adicionar() {
    if (tipo === 'pessoa' && !pessoaChave) return;
    if (tipo === 'patrimonio' && !patrimonioId) return;
    if (tipo === 'aluguel' && !descricaoAluguel.trim()) return;
    if (!dataInicio || !dataFim) return;
    let pessoaEmail: string | null = null;
    let pessoaExternaId: string | null = null;
    if (tipo === 'pessoa') {
      if (pessoaChave.startsWith('int:'))      pessoaEmail = pessoaChave.slice(4);
      else if (pessoaChave.startsWith('ext:')) pessoaExternaId = pessoaChave.slice(4);
    }
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_alocar_recurso',
          atividade_id: atividadeId,
          pessoa_email: pessoaEmail,
          pessoa_externa_id: pessoaExternaId,
          patrimonio_id: tipo === 'patrimonio' ? patrimonioId : null,
          descricao_aluguel: tipo === 'aluguel' ? descricaoAluguel.trim() : null,
          data_inicio: dataInicio, data_fim: dataFim,
          horas_dia: horasDia },
        { headers: { Authorization: token } });
      message.success('Recurso alocado.');
      setPessoaChave(''); setPatrimonioId(''); setDescricaoAluguel('');
      setAdicionando(false); setHorasDia(8);
      await recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function remover(id: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_remover_alocacao', id },
        { headers: { Authorization: token } });
      message.success('Removido.');
      await recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  const patrCats: Record<string, RecursosBundle['patrimonios']> = {};
  for (const p of recursos.patrimonios) {
    if (!patrCats[p.categoria]) patrCats[p.categoria] = [];
    patrCats[p.categoria].push(p);
  }

  return (
    <Card size="small" style={{ background: '#FAFBFF' }}
      title={<Typography.Text strong style={{ fontSize: 11, color: '#020D7F', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        🚐 Recursos alocados ({alocacoes.length})
      </Typography.Text>}
      extra={
        <Button size="small" type={adicionando ? 'default' : 'primary'}
          icon={adicionando ? undefined : <PlusOutlined />}
          onClick={() => setAdicionando(a => !a)}>
          {adicionando ? 'Cancelar' : 'Alocar'}
        </Button>
      }>
      {adicionando && (
        <Card size="small" style={{ marginBottom: 10 }}>
          <Segmented value={tipo} onChange={(v) => setTipo(v as any)} block
            options={[
              { label: 'Pessoa', value: 'pessoa', icon: <TeamOutlined /> },
              { label: 'Equipamento', value: 'patrimonio', icon: <ToolOutlined /> },
              { label: 'Aluguel', value: 'aluguel', icon: <ShoppingOutlined /> },
            ]} />

          <div style={{ marginTop: 10 }}>
            {tipo === 'pessoa' ? (
              <Select value={pessoaChave || undefined} onChange={(v) => setPessoaChave(v)}
                placeholder="— escolha uma pessoa —" style={{ width: '100%' }}
                showSearch optionFilterProp="label"
                options={[
                  {
                    label: '👤 Internos',
                    options: recursos.pessoas.map(p => ({
                      value: `int:${p.email}`,
                      label: `${p.email.split('@')[0]}${p.cargo ? ` · ${p.cargo}` : ''}`,
                    })),
                  },
                  ...(recursos.pessoas_externas?.length ? [{
                    label: '🤝 Externos',
                    options: recursos.pessoas_externas.map(p => ({
                      value: `ext:${p.id}`,
                      label: `${p.nome}${p.cargo ? ` · ${p.cargo}` : ''}${p.expertises?.length > 0 ? ` · [${p.expertises.slice(0,3).join(', ')}]` : ''}`,
                    })),
                  }] : []),
                ]} />
            ) : tipo === 'patrimonio' ? (
              <Select value={patrimonioId || undefined} onChange={(v) => setPatrimonioId(v)}
                placeholder="— escolha um equipamento —" style={{ width: '100%' }}
                showSearch optionFilterProp="label"
                options={Object.entries(patrCats).map(([cat, items]) => ({
                  label: cat,
                  options: items.map(p => ({
                    value: p.id,
                    label: `${p.descricao}${p.codigo_patrimonio ? ` · #${p.codigo_patrimonio}` : ''}`,
                  })),
                }))} />
            ) : (
              <Input value={descricaoAluguel}
                onChange={e => setDescricaoAluguel(e.target.value)}
                placeholder="Ex: Drone Mavic 3 alugado da Topograf — R$ 800/diária" />
            )}
          </div>

          <Row gutter={6} style={{ marginTop: 8 }}>
            <Col span={9}>
              <DatePicker value={dataInicio ? dayjs(dataInicio) : null}
                onChange={(d) => setDataInicio(d ? d.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Início" />
            </Col>
            <Col span={9}>
              <DatePicker value={dataFim ? dayjs(dataFim) : null}
                onChange={(d) => setDataFim(d ? d.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Fim" />
            </Col>
            <Col span={6}>
              <InputNumber value={horasDia}
                onChange={(v) => setHorasDia(typeof v === 'number' ? v : 8)}
                min={1} max={24} step={0.5} style={{ width: '100%' }}
                addonAfter="h/d" />
            </Col>
          </Row>

          <Button block type="primary" onClick={adicionar} style={{ marginTop: 10 }}
            disabled={
              (tipo === 'pessoa' && !pessoaChave) ||
              (tipo === 'patrimonio' && !patrimonioId) ||
              (tipo === 'aluguel' && !descricaoAluguel.trim()) ||
              !dataInicio || !dataFim
            }>
            Confirmar alocação
          </Button>
        </Card>
      )}

      {loading ? (
        <Spin />
      ) : alocacoes.length === 0 ? (
        <Empty description='Nenhum recurso alocado. Clique em "+ Alocar" pra adicionar pessoa ou equipamento.' />
      ) : (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {alocacoes.map(a => {
            const isExterna = !!a.pessoa_externa_id;
            const isInterna = !!a.pessoa_email;
            const isAluguel = !!a.descricao_aluguel;
            const isPessoa = isInterna || isExterna;
            const emoji = isAluguel ? '📦' : isPessoa ? '👤' : '🚐';
            const nome = isAluguel ? a.descricao_aluguel
              : isExterna ? (a.pessoa_externa_nome || 'Pessoa externa')
              : isInterna ? (a.pessoa_email!.split('@')[0])
              : (a.patrimonio_descricao || 'Equipamento');
            const subtitulo = isAluguel ? 'Aluguel externo'
              : isExterna ? (a.pessoa_externa_cargo || a.pessoa_externa_expertises?.slice(0, 2).join(', ') || 'Externo')
              : isInterna ? (a.pessoa_cargo || '')
              : `${a.patrimonio_categoria || ''}${a.codigo_patrimonio ? ` · #${a.codigo_patrimonio}` : ''}`;
            return (
              <Card key={a.id} size="small"
                styles={{ body: { padding: '6px 8px' } }}
                style={{ background: isAluguel ? '#FFF8E8' : '#fff',
                  borderColor: isAluguel ? '#F0D88E' : '#e0e0e0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr auto auto',
                  gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 14 }}>{emoji}</span>
                  <div style={{ minWidth: 0 }}>
                    <Typography.Text strong style={{ fontSize: 12 }} ellipsis>
                      {nome}
                      {isExterna && <Tag color="purple" style={{ marginLeft: 6, fontSize: 9 }}>externo</Tag>}
                      {isAluguel && <Tag color="orange" style={{ marginLeft: 6, fontSize: 9 }}>aluguel</Tag>}
                    </Typography.Text>
                    <div><Typography.Text type="secondary" style={{ fontSize: 10 }}>{subtitulo}</Typography.Text></div>
                  </div>
                  <div style={{ fontSize: 10, color: '#666', textAlign: 'right' }}>
                    {fmtData(a.data_inicio)} → {fmtData(a.data_fim)}
                    <div><Typography.Text type="secondary" style={{ fontSize: 9 }}>{a.horas_dia}h/dia</Typography.Text></div>
                  </div>
                  <Popconfirm title="Remover essa alocação?" onConfirm={() => remover(a.id)}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </div>
              </Card>
            );
          })}
        </Space>
      )}
    </Card>
  );
}
